import ContainerScreen from "@/main/components/shared/containerScreen/containerLayout";
import { KogTabsComponent } from "@/main/components/shared/tabs/tabs";
import { getTranslateByKey, isShowControlByElementId } from "@/main/services/utils";
import { useEffect, useState } from "react";
import lodash from "lodash";
import TabSupervisorRegister from "./tabSupervisorRegister";
import {
	ISupervisorRegisterForm,
	SupervisorRegisterProvider,
	useSupervisorRegisterContext,
} from "./supervisorRegisterContext";
import TabSupervisorSettings from "./tabSupervisorSettings";
import { supervisorApi } from "@/main/api/supervisorApi";
import { useFormContext } from "react-hook-form";
import { formattedContactGet, IContactInfoState } from "@/main/components/shared/contactInfo";
import { IAddressInfoState } from "@/main/components/shared/addressInfo";
import { InsurerBySupervisorResponse } from "@/main/models/response/supervisorResponse";
import { useSearchParams } from "react-router-dom";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import { Controls } from "@/main/services/constants/constantsControl";
import useKogControlElementId from "@/main/hooks/useKogControlElementId";
import { useUserReducer } from "@/main/store/user/userHooks";
import { CompanyTypeEnum } from "@/main/services/enum/CompanyTypeEnum";
import LayoutErrorScreen from "@/main/components/layouts/layoutErrorScreen/layoutErrorScreen";
import { RoutesConstants } from "@/main/routes/routesConstants";
import CompanyHistorical from "@/main/components/shared/companyHistorical/companyHistorical";

function SupervisorRegisterScreen() {
	// #region hooks
	const { isEdit, setIsEdit } = useSupervisorRegisterContext();
	const methods = useFormContext<ISupervisorRegisterForm>();

	const [searchParams] = useSearchParams();
	const guidSupervisorParams = searchParams.get("supervisorGuid");

	const showTabSettings = useKogControlElementId(Controls.IdTabSettings);
	// #endregion

	// #region states
	const [dataContacts, setDataContacts] = useState<IContactInfoState[]>([]);
	const [initialDataContacts, setInitialDataContacts] = useState<IContactInfoState[]>([]);
	const [dataAddress, setDataAddress] = useState<IAddressInfoState[]>([]);
	const [initialDataAddress, setInitialDataAddress] = useState<IAddressInfoState[]>([]);
	const [dataInsurers, setDataInsurers] = useState<InsurerBySupervisorResponse[]>([]);
	const [isLoadingInfo, setIsLoadingInfo] = useState<boolean>(false);

	const [guidCompany, setGuidCompany] = useState<string>("");

	const [isBlockedScreen, setBlockedScreen] = useState<boolean>(false);
	const userReducer = useUserReducer();

	// #endregion

	const guidInsurer =
		userReducer.currentUser?.idCompanyType == CompanyTypeEnum.Insurer ? userReducer.currentUser?.guidCompany : null;

	function loadInfoSupervisor(guid: string, guidInsurer: string) {
		setIsLoadingInfo(true);
		supervisorApi
			.getSupervisorData({ guid, guidInsurerLog: guidInsurer })
			.then((response) => {
				const { result } = response.data;

				methods.reset({
					country: {
						key: result.country.guid,
						value: result.country.name,
					},
					name: result.name,
					rfc: result.registrationNumber,
					statusRegister: {
						key: result.statusRegister.idStatusRegister,
						value: result.statusRegister.name,
					},
				});

				const formattedContacts = result.contacts?.map((contact) => formattedContactGet(contact));

				const formattedAddress = result.addresses?.map((address) => ({
					id: address.guid,
					address: address.street,
					complement: address.complement,
					number: address.number === "" ? null : address.number,
					zipCode: address.municipality.zipCode,
					addressType: {
						key: address.addressType.idAddressType,
						value: address.addressType.name,
					},
					city: {
						key: address.municipality.city.guid,
						value: address.municipality.city.name,
					},
					municipality: {
						key: address.municipality.guid,
						value: address.municipality.name,
					},
					state: {
						key: address.municipality.city.state.guid,
						value: address.municipality.city.state.name,
					},
				})) as IAddressInfoState[];

				const contacts = formattedContacts ?? [];
				setDataContacts(lodash.cloneDeep(contacts));
				setInitialDataContacts(lodash.cloneDeep(contacts));
				const addresses = formattedAddress ?? [];
				setDataAddress(lodash.cloneDeep(addresses));
				setInitialDataAddress(lodash.cloneDeep(addresses));
				setGuidCompany(result.guidCompany);
				setBlockedScreen(result.hasOtherCompanyRelation);
			})
			.catch(() => {})
			.finally(() => {
				setIsLoadingInfo(false);
			});
	}

	function loadInsurersBySupervisor(guid: string, guidInsurer: string) {
		supervisorApi
			.getAllInsurerBySupervisor(guid, guidInsurer)
			.then((response) => {
				const { result } = response.data;
				setDataInsurers(result);
			})
			.catch(() => {});
	}

	useEffect(() => {
		if (guidSupervisorParams) {
			setIsEdit(true);
			loadInfoSupervisor(guidSupervisorParams, guidInsurer ?? "");
			loadInsurersBySupervisor(guidSupervisorParams, guidInsurer ?? "");
		}
	}, [guidSupervisorParams]);

	if (isLoadingInfo) {
		return <LoadingComponent />;
	}

	if (isBlockedScreen) {
		return (
			<LayoutErrorScreen
				message={getTranslateByKey("information.supervisorAssociatedWithAnotherInsurance", true)}
				returnTo={RoutesConstants.registrationSearchSupervisor}
			/>
		);
	}

	const INTIAL_TAB_GENERAL_INFO: number = 0;
	const tabPanels = [
		{
			tab: getTranslateByKey("control.tabGeneralInfo"),
			panel: (
				<TabSupervisorRegister
					dataAddress={dataAddress}
					initialDataAddress={initialDataAddress}
					dataContacts={dataContacts}
					initialDataContacts={initialDataContacts}
					dataInsurers={dataInsurers}
					guidSupervisorParams={guidSupervisorParams}
					loadInsurersBySupervisor={loadInsurersBySupervisor}
					setDataAddress={setDataAddress}
					setDataContacts={setDataContacts}
					loadInfoSupervisor={loadInfoSupervisor}
				/>
			),
			isShowControl: true,
		},
		{
			tab: getTranslateByKey("control.tabSettings"),
			panel: <TabSupervisorSettings guidCompany={guidCompany} />,
			isShowControl: isEdit && showTabSettings,
		},
		{
			tab: getTranslateByKey("control.historical"),
			panel: <CompanyHistorical guidCompany={guidCompany} />,
			isShowControl: isEdit && isShowControlByElementId(Controls.IdCompanyHistoricalSupervisor),
		},
	];

	return (
		<ContainerScreen
			hasBackground
			title={`${getTranslateByKey("information.registration")} > ${getTranslateByKey("screen.supervisorRegistration")}`}
		>
			<KogTabsComponent tabPanels={tabPanels} tabCurrent={INTIAL_TAB_GENERAL_INFO} onChangeTab={() => {}} />
		</ContainerScreen>
	);
}

export default function SupervisorRegisterScreenLoaded() {
	return (
		<SupervisorRegisterProvider>
			<SupervisorRegisterScreen />
		</SupervisorRegisterProvider>
	);
}
